/**
 * UI-only projection of independently approved safeguarding subject coverage.
 *
 * One verified canonical training record can cover both Care Certificate
 * safeguarding subjects. Coverage never adds a second training record,
 * verification decision, completion date, or work-readiness override.
 */
const SUBJECTS = Object.freeze({
  adults: { code: 'safeguarding_adults', label: 'Safeguarding Adults' },
  children: { code: 'safeguarding_children', label: 'Safeguarding Children' },
});

export const safeguardingSubjectsForScope = (scope) => {
  const key = String(scope || '').trim().toLowerCase();
  if (key === 'combined') return [SUBJECTS.adults, SUBJECTS.children];
  if (key === 'adults') return [SUBJECTS.adults];
  if (key === 'children') return [SUBJECTS.children];
  return [];
};

export const verifiedSafeguardingCoverage = (record) => {
  if (!record || record.verified !== true && record.is_verified !== true ||
    record.verification_status === 'rejected' ||
    ['superseded', 'deleted'].includes(String(record.record_status || '').toLowerCase()) ||
    ['expired', 'rejected', 'missing', 'awaiting_review', 'pending'].includes(
      String(record.status || '').toLowerCase()
    )) return [];

  const code = String(record.code || record.requirement_id ||
    record.mapped_training_code || '').toLowerCase();
  const title = String(record.title || record.training_name || '').toLowerCase();
  if (!code.includes('safeguard') && !title.includes('safeguard')) return [];
  // Mandatory Training projects the UNION of current verified scope-specific
  // evidence from the existing canonical backend evaluator. A worker may have
  // separate Adults and Children certificates while Mandatory still has one
  // Safeguarding requirement.
  if (Array.isArray(record.safeguarding_covered_subjects)) {
    const set = new Set(record.safeguarding_covered_subjects);
    return [SUBJECTS.adults, SUBJECTS.children].filter(subject => set.has(subject.code));
  }
  // Only a recorded, reviewed coverage scope is authoritative.
  // A generic title is not proof of either subject.
  return safeguardingSubjectsForScope(record.safeguarding_scope);
};

/**
 * A manager's recorded subject choice is different from whether a course is
 * current for work. If a reviewed Children-only scope has not yet reconciled
 * into the evaluator coverage array, show a current-evidence warning instead
 * of inaccurately demanding another *subject review*.
 * Do not award Adults coverage or claim that a stale record is current.
 */
export const safeguardingReviewedScope = (record) => {
  const scope = String(record?.safeguarding_scope || '').trim().toLowerCase();
  return ['adults', 'children', 'combined'].includes(scope) ? scope : null;
};

/** UI-only: a verified generic course still needs the original subject review. */
export const verifiedSafeguardingNeedsScopeReview = (record, currentRequirementSatisfied) =>
  String(record?.code || '').toLowerCase() === 'safeguarding'
  && currentRequirementSatisfied === true
  && !safeguardingReviewedScope(record)
  && verifiedSafeguardingCoverage(record).length === 0;

/** Only re-read the matrix for the SAME employee; other tabs may be editing others. */
export const isSafeguardingScopeReviewForEmployee = (eventDetail, employeeId, workspaceId = '') => {
  if (!employeeId || !eventDetail || eventDetail.employeeId !== employeeId) return false;
  // Different tenants can legitimately reuse employee IDs. Do not let an
  // event in one workspace refresh another workspace's training UI.
  const sourceWorkspace = String(eventDetail.workspaceId || '').trim();
  const currentWorkspace = String(workspaceId || '').trim();
  if (sourceWorkspace || currentWorkspace) {
    return Boolean(sourceWorkspace && currentWorkspace && sourceWorkspace === currentWorkspace);
  }
  return true;
};
